import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { toast } from "sonner";
import { YOUTUBE_VIDEO_ID } from "../config";
import LeadFormModal from "../components/LeadFormModal";
import MenuDrawer from "../components/MenuDrawer";

const LOGO_SRC = "/neXstrength-logo.png";
const VIDEO_POSTER =
  "https://images.pexels.com/photos/36813102/pexels-photo-36813102.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + 0.11 * i, duration: 0.85, ease: EASE },
  }),
};

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.3 + 0.15 * i, duration: 0.95, ease: EASE },
  }),
};

export default function LandingPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: videoRef,
    offset: ["start end", "end start"],
  });
  const parY = useTransform(scrollYProgress, [0, 1], [-12, 12]);

  const handlePlay = () => {
    if (YOUTUBE_VIDEO_ID) setPlaying(true);
    else toast.info("The VSL video will play right here — video link coming soon.");
  };

  return (
    <div className="relative min-h-screen bg-white text-[#0A0A0A]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px 480px at 50% -8%, rgba(242,92,5,0.07), transparent 65%)",
        }}
      />

      <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pb-14 pt-9 text-center sm:pt-14">
        {/* Logo lockup */}
        <motion.div
          variants={fadeUp}
          custom={0}
          initial="hidden"
          animate="show"
          className="flex items-center justify-center gap-3 sm:gap-3.5"
        >
          <img
            src={LOGO_SRC}
            alt="NeXstrength logo"
            data-testid="brand-logo"
            className="h-[58px] w-auto object-contain sm:h-[66px]"
          />
          <div className="flex flex-col items-stretch text-left">
            <div className="text-[28px] font-extrabold leading-none tracking-[-0.02em] sm:text-[32px]">
              Ne<span className="text-[#F25C05]">X</span>strength
            </div>
            <div
              data-testid="brand-tagline"
              className="mt-[7px] flex w-full justify-between text-[8.5px] font-semibold leading-none sm:text-[9.5px]"
            >
              {"BUSINESS CONSULTANCY".split("").map((ch, i) =>
                ch === " " ? (
                  <span key={i} className="w-[5px]" />
                ) : (
                  <span key={i}>{ch}</span>
                )
              )}
            </div>
          </div>
        </motion.div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="mt-8 text-[clamp(2.05rem,8.6vw,3.05rem)] font-extrabold leading-[1.07] tracking-[-0.03em] sm:mt-10"
        >
          <span className="block overflow-hidden pb-0.5">
            <motion.span
              className="block"
              variants={lineReveal}
              custom={0}
              initial="hidden"
              animate="show"
            >
              Take Your Business to
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block text-[#F25C05]"
              variants={lineReveal}
              custom={1}
              initial="hidden"
              animate="show"
            >
              ₹10 Lakh Monthly
            </motion.span>
          </span>
        </h1>

        {/* Sub copy */}
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          animate="show"
          className="mt-6 max-w-[460px] text-[13px] font-medium leading-[1.65] text-[#141414] [text-wrap:balance] sm:text-[16px]"
        >
          You started a business for financial freedom, but now you work longer
          hours, carry more stress, and still can't afford the lifestyle you
          dreamed of. It's time to change that.
        </motion.p>

        {/* VSL video */}
        <motion.div
          ref={videoRef}
          style={{ y: parY }}
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate="show"
          className="mt-8 w-full sm:mt-10"
        >
          <div
            data-testid="vsl-video-card"
            onClick={handlePlay}
            className="group relative aspect-[5/3] w-full cursor-pointer overflow-hidden rounded-[22px] bg-[#1a120b] shadow-[0_30px_60px_-22px_rgba(242,92,5,0.4)]"
          >
            {playing ? (
              <iframe
                data-testid="vsl-youtube-iframe"
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                title="VSL Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img
                  src={VIDEO_POSTER}
                  alt="VSL video preview"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/15" />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="relative">
                    <motion.span
                      className="absolute inset-0 rounded-full bg-white/40"
                      animate={{ scale: [1, 1.55], opacity: [0.45, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                    />
                    <div
                      data-testid="vsl-play-button"
                      className="relative flex h-[72px] w-[72px] items-center justify-center rounded-full bg-white shadow-xl transition-transform duration-300 group-hover:scale-105 sm:h-[84px] sm:w-[84px]"
                    >
                      <Play className="ml-1 h-7 w-7 fill-[#F25C05] text-[#F25C05] sm:h-8 sm:w-8" />
                    </div>
                  </div>
                  <span className="mt-4 text-[12px] font-semibold tracking-[0.38em] text-white sm:text-[13px]">
                    VSL&nbsp;VIDEO
                  </span>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-9 w-full sm:mt-10">
          <button
            data-testid="book-call-button"
            onClick={() => setFormOpen(true)}
            className="group relative w-full overflow-hidden rounded-[18px] bg-gradient-to-b from-[#FF8A3C] to-[#F25C05] py-[17px] text-[18px] font-bold text-white shadow-[0_16px_36px_-10px_rgba(242,92,5,0.55)] transition-shadow duration-300 hover:shadow-[0_22px_46px_-10px_rgba(242,92,5,0.7)] active:scale-[0.985] sm:py-5 sm:text-[20px]"
          >
            <span className="relative z-10 inline-flex items-center gap-2.5">
              Book a Personal Call
              <ArrowRight
                className="h-[21px] w-[21px] transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.6}
              />
            </span>
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
          </button>
        </motion.div>

        {/* Disclaimer */}
        <motion.div variants={fadeUp} custom={5} initial="hidden" animate="show" className="mt-10 w-full">
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-[#E7E7E7]" />
            <span className="text-[13px] font-bold">Disclaimer</span>
            <span className="h-px flex-1 bg-[#E7E7E7]" />
          </div>
          <p className="mx-auto mt-3.5 max-w-[420px] text-center text-[12.5px] font-medium leading-[1.62] text-[#8F8F8F] [text-wrap:balance]">
            Results vary based on individual effort, business model, market
            conditions and execution. This is a consultancy service and does
            not guarantee specific revenue or financial outcomes.
          </p>
        </motion.div>
      </main>

      <MenuDrawer />
      <LeadFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
